import type { ChamberTrack } from '@ctcells/contract'
import { enumLabel, moduleLabel, t } from '@ctcells/i18n'

import type { ChamberStage } from '../../figures/chamber3d/chamberScene.ts'

import type { ChamberProgress, ChamberProgressStatus } from './chamberProgress.ts'
import { NOTE_DEVICE, type ChamberStageNote } from './flowVocabulary.ts'

const MARKS: Record<string, { stage: ChamberStage; status: ChamberProgressStatus }> = {
  centrifuge_waiting: { stage: 'centrifuged', status: 'pending' },
  centrifuge_spinning: { stage: 'centrifuged', status: 'settled' },
  centrifuge_done: { stage: 'centrifuged', status: 'done' },
  assembler_waiting: { stage: 'assembled', status: 'pending' },
  assembler_cycle_running: { stage: 'assembled', status: 'settled' },
  assembler_cycle_done: { stage: 'assembled', status: 'settled' },
  assembler_draining: { stage: 'assembled', status: 'settled' },
  assembler_done: { stage: 'assembled', status: 'done' },
}

function chamberStage(value: string | null | undefined): ChamberStage | undefined {
  switch (value) {
    case 'registered':
    case 'centrifuged':
    case 'assembled':
    case 'staining':
    case 'completed':
      return value
    case 'stained':
      return 'staining'
    default:
      return undefined
  }
}

export function trackingChamberProgress(
  track: ChamberTrack | undefined,
): ChamberProgress | undefined {
  if (track === undefined) return undefined
  const mark = track.phase_mark == null ? undefined : MARKS[track.phase_mark]
  const activeStage = chamberStage(track.active?.stage)
  /*
   * 🔴 끝난 웰은 «완료» 단계다 — 2026-10-23 실측(작업 5a5e38ca · completed)에서 9칸 전부 `stage: completed` 인데
   *    `recipe_step` 25/25 가 남아 왔다. 레시피 단계가 있다고 염색 단계로 되돌리지 않는다(한 마디 글자는 그대로 «단계 25/25 · 시약»).
   */
  const stage =
    activeStage ??
    (track.stage === 'completed'
      ? 'completed'
      : track.recipe_step == null
        ? mark?.stage
        : 'staining') ??
    chamberStage(track.stage)
  if (stage === undefined) return undefined
  const status: ChamberProgressStatus =
    track.condition !== 'normal'
      ? 'failed'
      : activeStage !== undefined
        ? 'active'
        : track.recipe_step != null
          ? track.stage === 'completed' || track.stage === 'stained'
            ? 'done'
            : 'settled'
          : (mark?.status ??
            (stage === 'registered'
              ? 'pending'
              : stage === 'staining' && track.stage !== 'stained'
                ? 'settled'
                : 'done'))
  return { stage, status, basis: 'tracking', track }
}

export function trackingChamberNote(progress: ChamberProgress): ChamberStageNote | undefined {
  const track = progress.track
  if (track === undefined) return undefined
  const device = NOTE_DEVICE[progress.stage]
  if (track.condition !== 'normal') {
    const condition = track.condition
    return {
      device,
      word:
        condition === 'discarded' || condition === 'awaiting_decision'
          ? enumLabel('enum.sampleStatus', condition)
          : condition === 'needs_check'
            ? t('row.ackRequired')
            : t('monitoring.flow.cell.fail'),
      tone:
        condition === 'discarded'
          ? 'halted'
          : condition === 'awaiting_decision'
            ? 'behind'
            : 'fail',
    }
  }
  const step = track.recipe_step
  if (step != null) {
    const label = step.label ?? step.liquid_id
    return {
      device,
      word: [t('monitoring.chamber.recipeStep', { no: step.no, total: step.total }), label]
        .filter(Boolean)
        .join(' · '),
      status: noteStatus(progress.status),
    }
  }
  const doing = trackingActiveWord(track)
  if (doing !== undefined && track.phase_mark != null)
    return {
      device,
      word: doing,
      status: noteStatus(progress.status),
      /* 마디 글자는 툴팁에 남긴다 — 「완료 · 회수 중」(장비는 앞 칩이 말한다 · eric 2026-10-29) */
      title: `${enumLabel('monitoring.phaseMark', track.phase_mark)} · ${doing}`,
    }
  if (track.phase_mark != null)
    return {
      device,
      word: enumLabel('monitoring.phaseMark', track.phase_mark),
      status: noteStatus(progress.status),
    }
  /* 마디 · 레시피 단계 없이 그 단계 안(`settled`) — 공정명만(종전 모듈 근거의 `stage` 한 마디와 같은 꼴) */
  if (progress.status === 'settled')
    return { device, word: moduleLabel(device), status: noteStatus(progress.status) }
  return {
    device,
    word: t(
      progress.status === 'active'
        ? 'monitoring.chamber.doing'
        : progress.status === 'done'
          ? 'monitoring.chamber.done'
          : 'monitoring.chamber.ready',
    ),
    status: noteStatus(progress.status),
  }
}

/**
 * **마디 + `active` 조합 → 그 웰을 지금 무엇 하는 중인가**(서버 어휘 · 모듈 상태 이름표 `enum.moduleState`).
 *
 * 🔴 2026-10-23 로컬 실측(작업 0ecfa832 · 원심 3회차 9웰 · `GET /batches/{id}/tracking` 2.5초 간격): 원심 단계 웰마다
 *    `centrifuge_waiting` + `active{centrifuged}` (한 웰씩 차례로) → `centrifuge_spinning`(`active` 없음 · 웰 넷이 같이) →
 *    `centrifuge_done` + `active{centrifuged}` (다시 한 웰씩 차례로). 마디의 정의(규칙 `process.phase_marks` — `run_centrifugation`
 *    시작 전 / 완료 뒤)와 `active`(그 웰의 끝나지 않은 원자)를 겹치면 앞은 로터에 싣는 중, 뒤는 로터에서 꺼내는 중이다.
 *    그 말은 서버 어휘에 있다 — 원심 모듈 상태 `LOADING`(적재 중) · `UNLOADING`(회수 중).
 * 🔴 «완료» 마디에 `active` 가 서면 「원심 완료」에 진행 배지가 붙어 「다 끝났는데 실행 중」으로 읽혔다(eric 지적) — 이 낱말로 바꾼다.
 * ⚠ 어셈블러 마디(사이클 완료 · 완료 + `active`)는 아직 실측하지 못했다 — 짐작해 낱말을 붙이지 않는다(표에 없으면 `undefined` ·
 *   마디 글자 그대로). 실측하면 이 표에 더한다.
 */
const ACTIVE_WORD: Readonly<Record<string, string>> = {
  centrifuge_waiting: 'LOADING',
  centrifuge_done: 'UNLOADING',
}

export function trackingActiveWord(
  track: Pick<ChamberTrack, 'active' | 'phase_mark' | 'condition'> | undefined,
): string | undefined {
  if (track?.active == null || track.phase_mark == null || track.condition !== 'normal')
    return undefined
  const state = ACTIVE_WORD[track.phase_mark]
  return state === undefined ? undefined : enumLabel('enum.moduleState', state)
}

/**
 * 진행 → 한 마디 배지 종류. 🔴 **`settled` 는 「그 단계 안에 와 있고 지금 그 웰을 하지는 않는다」다** — 회전 중 · 사이클 중 ·
 * 폐액 배출 중 마디(서버 `active` 없음)와, 시약 k/N 까지 받고 다음 차례를 기다리는 염색 웰이 여기 온다. 「준비 중」(`ready`)
 * 배지를 달면 «아직 시작 전»이라는 거짓이 된다(2026-10-23 시연 · Codex 판은 `ready` 였다) — 배지 없이 서버 글자만 선다(`stage`).
 */
function noteStatus(status: ChamberProgressStatus): NonNullable<ChamberStageNote['status']> {
  switch (status) {
    case 'active':
      return 'doing'
    case 'done':
      return 'done'
    case 'settled':
      return 'stage'
    default:
      return 'ready'
  }
}
